import {
  type ComponentProps,
  type ComponentType,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  AccessibilityInfo,
  Animated,
  Easing,
  Platform,
  StyleSheet,
  View,
} from "react-native";
import Svg, { Defs, LinearGradient, Path, Rect, Stop } from "react-native-svg";

import { fontFamily } from "@passfirst/ui";

// Animated brand splash — recreates the splash handoff timeline
// (docs/design_refresh/design_handoff_brand_graphics/splash). Plays ONCE
// on cold start, then fades out to reveal the app. The deep-navy ground
// (#06101D) matches the native static splash frame (app.json + the iOS
// SplashScreenBackground colorset), so the native→JS handoff is seamless.
//
// No Reanimated/Lottie in this app — driven with the built-in Animated
// API + an animated react-native-svg path for the tick "draw". The path
// length is ~52 (M17 34 L28 45 L50 17 on a 64 grid), animated via
// strokeDashoffset 52 → 0. strokeDashoffset isn't a transform/opacity,
// so that leg can't use the native driver; the rest does where it can.

const SPLASH_BG = "#06101D";
const TICK_LEN = 52;
// StyleSheet.absoluteFillObject isn't on the react-native-web types pulled in
// under the SDK-56 type split; inline the well-known constant.
const ABSOLUTE_FILL = {
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
} as const;
// react-native-svg's Path component type doesn't satisfy
// createAnimatedComponent under @types/react@19 (SDK 56); it's valid at
// runtime. Cast through the component's own props.
const AnimatedPath = Animated.createAnimatedComponent(
  Path as unknown as ComponentType<ComponentProps<typeof Path>>,
);

interface Props {
  /** Called once the overlay has fully faded out and can be unmounted. */
  onDone: () => void;
}

export function SplashOverlay({ onDone }: Props): JSX.Element {
  const tileScale = useRef(new Animated.Value(0.62)).current;
  const tileOpacity = useRef(new Animated.Value(0)).current;
  const dash = useRef(new Animated.Value(TICK_LEN)).current;
  const ringScale = useRef(new Animated.Value(0.6)).current;
  const ringOpacity = useRef(new Animated.Value(0)).current;
  const ring2Scale = useRef(new Animated.Value(0.6)).current;
  const ring2Opacity = useRef(new Animated.Value(0)).current;
  const wordY = useRef(new Animated.Value(15)).current;
  const wordOpacity = useRef(new Animated.Value(0)).current;
  const tagOpacity = useRef(new Animated.Value(0)).current;
  const tagSpacing = useRef(new Animated.Value(4.8)).current;
  const stageOpacity = useRef(new Animated.Value(1)).current;

  // Reduced motion: skip to the resolved frame, hold briefly, fade.
  const [reduceMotion, setReduceMotion] = useState(false);
  useEffect(() => {
    let alive = true;
    // `.catch` matters: on web with no matchMedia (some prerender paths)
    // this rejects, and the global observa handler would log a spurious
    // error. Default to motion-on.
    void AccessibilityInfo.isReduceMotionEnabled()
      .then((v) => {
        if (alive) setReduceMotion(v);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    const finish = () =>
      Animated.timing(stageOpacity, {
        toValue: 0,
        duration: 360,
        easing: Easing.in(Easing.cubic),
        useNativeDriver: Platform.OS !== "web",
      }).start(({ finished }) => {
        if (finished) onDone();
      });

    if (reduceMotion) {
      tileScale.setValue(1);
      tileOpacity.setValue(1);
      dash.setValue(0);
      wordY.setValue(0);
      wordOpacity.setValue(1);
      tagOpacity.setValue(1);
      tagSpacing.setValue(2.5);
      const t = setTimeout(finish, 700);
      return () => clearTimeout(t);
    }

    const native = Platform.OS !== "web";
    const seq = Animated.sequence([
      // Tile springs in (overshoot) + fades.
      Animated.parallel([
        Animated.spring(tileScale, {
          toValue: 1,
          friction: 6,
          tension: 80,
          useNativeDriver: native,
        }),
        Animated.timing(tileOpacity, {
          toValue: 1,
          duration: 280,
          useNativeDriver: native,
        }),
      ]),
      // Tick draws on; the forward-pulse rings expand + fade in parallel.
      Animated.parallel([
        Animated.timing(dash, {
          toValue: 0,
          duration: 560,
          easing: Easing.bezier(0.65, 0, 0.35, 1),
          // strokeDashoffset is not natively animatable.
          useNativeDriver: false,
        }),
        Animated.sequence([
          Animated.timing(ringOpacity, {
            toValue: 0.5,
            duration: 120,
            useNativeDriver: native,
          }),
          Animated.parallel([
            Animated.timing(ringScale, {
              toValue: 2,
              duration: 700,
              easing: Easing.out(Easing.cubic),
              useNativeDriver: native,
            }),
            Animated.timing(ringOpacity, {
              toValue: 0,
              duration: 700,
              useNativeDriver: native,
            }),
          ]),
        ]),
        // Second ring — staggered, expands further (the "↗ forward" pulse).
        Animated.sequence([
          Animated.delay(200),
          Animated.timing(ring2Opacity, {
            toValue: 0.32,
            duration: 120,
            useNativeDriver: native,
          }),
          Animated.parallel([
            Animated.timing(ring2Scale, {
              toValue: 2.4,
              duration: 800,
              easing: Easing.out(Easing.cubic),
              useNativeDriver: native,
            }),
            Animated.timing(ring2Opacity, {
              toValue: 0,
              duration: 800,
              useNativeDriver: native,
            }),
          ]),
        ]),
      ]),
      // Wordmark rises + fades.
      Animated.parallel([
        Animated.timing(wordY, {
          toValue: 0,
          duration: 360,
          easing: Easing.out(Easing.cubic),
          useNativeDriver: native,
        }),
        Animated.timing(wordOpacity, {
          toValue: 1,
          duration: 360,
          useNativeDriver: native,
        }),
      ]),
      // Tagline fades + letter-spacing tightens.
      Animated.parallel([
        Animated.timing(tagOpacity, {
          toValue: 1,
          duration: 320,
          useNativeDriver: false,
        }),
        Animated.timing(tagSpacing, {
          toValue: 2.5,
          duration: 320,
          useNativeDriver: false,
        }),
      ]),
      Animated.delay(420),
    ]);

    seq.start(({ finished }) => {
      if (finished) finish();
    });
    return () => seq.stop();
    // Animated refs are stable; run once on mount (or when reduceMotion resolves).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reduceMotion]);

  return (
    <Animated.View
      style={[styles.root, { opacity: stageOpacity }]}
      pointerEvents="none"
      accessibilityElementsHidden
      importantForAccessibility="no-hide-descendants"
    >
      {/* Ambient navy vignette. */}
      <View style={styles.vignette} />

      <View style={styles.stage}>
        {/* Forward-pulse rings behind the mark (two, staggered). */}
        <Animated.View
          style={[
            styles.ring,
            { opacity: ringOpacity, transform: [{ scale: ringScale }] },
          ]}
        />
        <Animated.View
          style={[
            styles.ring,
            styles.ring2,
            { opacity: ring2Opacity, transform: [{ scale: ring2Scale }] },
          ]}
        />
        <Animated.View
          style={{ opacity: tileOpacity, transform: [{ scale: tileScale }] }}
        >
          <Svg width={140} height={140} viewBox="0 0 64 64">
            <Defs>
              <LinearGradient id="pf-tile" x1="0" y1="0" x2="0" y2="1">
                <Stop offset="0" stopColor="#173559" />
                <Stop offset="1" stopColor="#0E2845" />
              </LinearGradient>
            </Defs>
            <Rect x="2" y="2" width="60" height="60" rx="17" fill="url(#pf-tile)" />
            <Rect
              x="2.75"
              y="2.75"
              width="58.5"
              height="58.5"
              rx="16.4"
              fill="none"
              stroke="rgba(255,255,255,0.08)"
              strokeWidth="1.2"
            />
            <AnimatedPath
              d="M17 34 L28 45 L50 17"
              fill="none"
              stroke="#FF6B47"
              strokeWidth="6.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeDasharray={TICK_LEN}
              strokeDashoffset={dash}
            />
          </Svg>
        </Animated.View>

        <Animated.Text
          style={[
            styles.wordmark,
            { opacity: wordOpacity, transform: [{ translateY: wordY }] },
          ]}
        >
          PassFirst
        </Animated.Text>
        <Animated.Text
          style={[
            styles.tagline,
            { opacity: tagOpacity, letterSpacing: tagSpacing },
          ]}
        >
          PASS ON THE FIRST TRY
        </Animated.Text>
      </View>
    </Animated.View>
  );
}

const styles = StyleSheet.create({
  root: {
    ...ABSOLUTE_FILL,
    backgroundColor: SPLASH_BG,
    alignItems: "center",
    justifyContent: "center",
    zIndex: 999,
  },
  vignette: {
    ...ABSOLUTE_FILL,
    backgroundColor: "#0C1E36",
    opacity: 0.5,
  },
  stage: {
    alignItems: "center",
    justifyContent: "center",
  },
  ring: {
    position: "absolute",
    width: 150,
    height: 150,
    borderRadius: 34,
    borderWidth: 2,
    borderColor: "rgba(255,107,71,0.55)",
  },
  ring2: {
    borderRadius: 40,
    borderWidth: 1.5,
    borderColor: "rgba(255,107,71,0.4)",
  },
  wordmark: {
    marginTop: 28,
    fontFamily: fontFamily.extraBold,
    fontSize: 36,
    letterSpacing: -0.7,
    color: "#FFFFFF",
  },
  tagline: {
    marginTop: 13,
    fontFamily: fontFamily.monoMedium,
    fontSize: 11.5,
    color: "#7F97B8",
  },
});
